import { Navigate, Outlet, type RouteObject } from "react-router";
import { AppShell } from "../components/layout/app-shell";
import { FullPageLoader } from "../components/layout/full-page-loader";
import { ChangePasswordPage } from "../features/auth/change-password-page";
import { LoginPage } from "../features/auth/login-page";
import { RequireAuth } from "../features/auth/require-auth";
import { SystemAdminLoginPage } from "../features/auth/system-admin-login-page";
import { DashboardPage } from "../features/dashboard/dashboard-page";
import { NotFoundPage, RouteErrorPage } from "../features/errors/error-pages";
import {
  LOGIN_PATH,
  SET_PASSWORD_PATH,
  SIGNING_PATH,
  SYSTEM_ADMIN_LOGIN_PATH,
} from "../lib/sign-in-paths";

const platformLayout = async () => ({
  Component: (await import("../features/platform/platform-common")).PlatformLayout,
});

/**
 * Platform console (Eduleo's own team). Every page sits behind PlatformLayout, which lets in
 * only people whose /auth/me has platform capabilities. Each page then checks its own permission.
 */
const platformRoutes: RouteObject = {
  path: "platform",
  lazy: platformLayout,
  children: [
    {
      index: true,
      lazy: async () => ({
        Component: (await import("../features/platform/overview-page")).PlatformOverviewPage,
      }),
    },
    {
      path: "schools",
      lazy: async () => ({
        Component: (await import("../features/platform/schools-page")).SchoolsPage,
      }),
    },
    {
      path: "schools/new",
      lazy: async () => ({
        Component: (await import("../features/platform/onboard-school-page")).OnboardSchoolPage,
      }),
    },
    {
      path: "schools/:code",
      lazy: async () => ({
        Component: (await import("../features/platform/school-page")).SchoolPage,
      }),
      children: [
        {
          index: true,
          lazy: async () => ({
            Component: (await import("../features/platform/school-overview-tab")).SchoolOverviewTab,
          }),
        },
        {
          path: "details",
          lazy: async () => ({
            Component: (await import("../features/platform/school-details-tab")).SchoolDetailsTab,
          }),
        },
        {
          path: "documents",
          lazy: async () => ({
            Component: (await import("../features/platform/school-documents-tab"))
              .SchoolDocumentsTab,
          }),
        },
        {
          path: "contracts",
          lazy: async () => ({
            Component: (await import("../features/platform/school-contracts-tab"))
              .SchoolContractsTab,
          }),
        },
        {
          path: "invoices",
          lazy: async () => ({
            Component: (await import("../features/platform/invoices-page")).SchoolInvoicesTab,
          }),
        },
        {
          path: "accounts",
          lazy: async () => ({
            Component: (await import("../features/platform/school-accounts-tab")).SchoolAccountsTab,
          }),
        },
      ],
    },
    {
      path: "invoices",
      lazy: async () => ({
        Component: (await import("../features/platform/invoices-page")).InvoicesPage,
      }),
    },
    {
      path: "invoices/new",
      lazy: async () => ({
        Component: (await import("../features/platform/invoice-form-page")).InvoiceFormPage,
      }),
    },
    {
      path: "invoices/:id",
      lazy: async () => ({
        Component: (await import("../features/platform/invoice-page")).InvoicePage,
      }),
    },
    {
      path: "invoices/:id/edit",
      lazy: async () => ({
        Component: (await import("../features/platform/invoice-form-page")).InvoiceFormPage,
      }),
    },
    {
      path: "team",
      lazy: async () => ({
        Component: (await import("../features/platform/team-page")).TeamPage,
      }),
    },
    {
      path: "regions",
      lazy: async () => ({
        Component: (await import("../features/platform/regions-page")).RegionsPage,
      }),
    },
    {
      path: "settings",
      lazy: async () => ({
        Component: (await import("../features/platform/settings-layout")).SettingsLayout,
      }),
      children: [
        {
          index: true,
          lazy: async () => ({
            Component: (await import("../features/platform/profile-page")).ProfileSettingsPage,
          }),
        },
        {
          path: "notifications",
          lazy: async () => ({
            Component: (await import("../features/platform/notification-settings-page"))
              .NotificationSettingsPage,
          }),
        },
        {
          path: "security",
          lazy: async () => ({
            Component: (await import("../features/platform/security-page")).SecurityPage,
          }),
        },
        {
          path: "company",
          lazy: async () => ({
            Component: (await import("../features/platform/company-page")).CompanyPage,
          }),
        },
      ],
    },
    // Where these pages were before System settings; links in old emails still work.
    { path: "security", element: <Navigate to="/platform/settings/security" replace /> },
    { path: "company", element: <Navigate to="/platform/settings/company" replace /> },
  ],
};

/** Contract and invoice print views: a clean A4 page, without the app's menus. */
const platformPrintRoutes: RouteObject = {
  element: (
    <RequireAuth>
      <Outlet />
    </RequireAuth>
  ),
  hydrateFallbackElement: <FullPageLoader />,
  errorElement: <RouteErrorPage />,
  children: [
    {
      lazy: platformLayout,
      children: [
        {
          path: "/platform/contracts/:id/print",
          lazy: async () => ({
            Component: (await import("../features/platform/contract-print-page")).ContractPrintPage,
          }),
        },
        {
          path: "/platform/invoices/:id/print",
          lazy: async () => ({
            Component: (await import("../features/platform/invoice-print-page")).InvoicePrintPage,
          }),
        },
      ],
    },
  ],
};

/**
 * Shared by the browser router and the tests' memory router.
 * Sign-in and Home load with the app; every other module loads when first opened,
 * so a parent on a slow phone doesn't download screens they will never use.
 */
export const routes: RouteObject[] = [
  // A school's signatory signs Eduleo's agreement from an emailed link: no sign-in, no app shell.
  {
    path: SIGNING_PATH,
    lazy: async () => ({
      Component: (await import("../features/signing/signing-page")).SigningPage,
    }),
    hydrateFallbackElement: <FullPageLoader />,
    errorElement: <RouteErrorPage />,
  },
  // Opened from an emailed "set your password" link: no sign-in, no app shell.
  {
    path: SET_PASSWORD_PATH,
    lazy: async () => ({
      Component: (await import("../features/auth/set-password-page")).SetPasswordPage,
    }),
    hydrateFallbackElement: <FullPageLoader />,
    errorElement: <RouteErrorPage />,
  },
  { path: LOGIN_PATH, element: <LoginPage />, errorElement: <RouteErrorPage /> },
  // Eduleo's own team signs in here; the school sign-in doesn't mention it.
  {
    path: SYSTEM_ADMIN_LOGIN_PATH,
    element: <SystemAdminLoginPage />,
    errorElement: <RouteErrorPage />,
  },
  // Outside RequireAuth on purpose: it is where RequireAuth sends people who must change it.
  { path: "/change-password", element: <ChangePasswordPage />, errorElement: <RouteErrorPage /> },
  platformPrintRoutes,
  {
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    // Shown while a page's code downloads on the first visit, instead of a blank screen.
    hydrateFallbackElement: <FullPageLoader />,
    errorElement: <RouteErrorPage />,
    children: [
      { index: true, element: <DashboardPage /> },
      {
        path: "students",
        lazy: async () => ({
          Component: (await import("../features/students/students-page")).StudentsPage,
        }),
      },
      {
        path: "settings/classes",
        lazy: async () => ({
          Component: (await import("../features/settings/classes-page")).ClassesPage,
        }),
      },
      {
        path: "settings/people",
        lazy: async () => ({
          Component: (await import("../features/settings/staff-page")).StaffPage,
        }),
      },
      {
        path: "settings/people/roles",
        lazy: async () => ({
          Component: (await import("../features/settings/roles-page")).RolesPage,
        }),
      },
      platformRoutes,
      { path: "*", element: <NotFoundPage /> },
    ],
  },
];
